import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Toast.module.css';

/**
 * Toast — notification.
 *   success / error: Flutter CustomFlushBar (CustomNotification, green / red, 3 s), including
 *     translating the message (appLocalization.translateString). Callers may pass Flutter's
 *     untranslated text; text that is already translated is not a key, so it comes back unchanged.
 *   info: Flutter AppConstants.showToast (e.g. "Copied to your clipboard !") — the tenant toast
 *     colour (--toast-bg, from highlightColor), white text, 1 s on web (timeInSecForIosWeb).
 */

type ToastType = 'success' | 'error' | 'info';

interface ToastProps {
  message: string;
  type: ToastType;
  onClose: () => void;
  duration?: number;
}

const TYPE_CLASS: Record<ToastType, string | undefined> = {
  success: styles.success,
  error: styles.error,
  info: styles.info,
};

export function Toast({ message, type, onClose, duration = type === 'info' ? 1000 : 3000 }: ToastProps) {
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [onClose, duration]);

  return (
    <div className={`${styles.toast} ${TYPE_CLASS[type] ?? ''}`} role={type === 'error' ? 'alert' : 'status'}>
      <span>{t(message)}</span>
      <button className={styles.close} onClick={onClose} aria-label={t('Close')}>
        ×
      </button>
    </div>
  );
}
